import { memo, useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  ModalBody,
  ModalContent,
  Modal,
  ModalHeader,
  ModalFooter,
  Tabs,
  Tab,
  Checkbox,
  Button,
  RadioGroup,
  Radio,
} from '@heroui/react';
import { CalendarDate, Time, parseTime } from '@internationalized/date';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { isActiveScormUpload, useStepScormUploadsFor } from '@/features/StepScormUploads';
import { isActiveUpload, useStepVideoUploadsFor } from '@/features/StepVideoUploads';
import {
  useUpdateLessonMutation,
  useLazyGetScormPreviewUrlQuery,
  Lesson,
  type LessonFormat,
  type ModuleIntakeTerm,
  type UpdateLessonRequest,
} from '@/entities/Courses';
import { useGetLocationsAllQuery } from '@/entities/Locations';
import OpenedEyeIcon from '@/shared/assets/icons/opened-eye.svg';
import { AppButton } from '@/shared/ui/AppButton';
import {
  CustomInput,
  CustomTextarea,
  CustomDatepicker,
  CustomTimeInput,
  CustomSelect,
  FieldHint,
} from '@/shared/ui/Fields';
import { Dropzone } from '@/shared/ui/Dropzone';
import { Divider } from '@/shared/ui/Divider';
import { MessageModal } from '@/shared/ui/Modal/MessageModal';
import { Svg } from '@/shared/ui/Svg';
import { scaleInOut } from '@/shared/lib/utils/modalTransitions';
import { useToast } from '@/shared/lib/hooks/useToast';
import { MAX_FILE_SIZE_HINT } from '@/shared/lib/constants/fileSizes';
import {
  combineDateAndTime,
  combineDateAndTimeAsUTC,
  parseISOToDateValueAndTime,
  parseISOToDateValueAndTimeAsStored,
} from '@/shared/lib/utils/dateAdapters';
import UploadVideoIcon from '@/shared/assets/icons/files/upload-video.svg';
import { SectionLoader } from '@/shared/ui/Loader';
import type { FileInfo } from '@/shared/types/files';
import {
  SESSION_ATTACHMENT_ACCEPT,
  SESSION_ATTACHMENT_TYPES_HINT,
} from '../../../config/sessionAttachmentAccept';
import { SCORM_STATUS_BADGE } from '../../../config/scormStatusBadge';
import {
  testDeadlineWithinTerm,
  testSessionDateWithinTerm,
  testSlotEndWithinTerm,
  formatDeadlineHint,
  formatSessionDateHint,
  getSessionDatePickerProps,
  useSessionTermDates,
  type SessionDatesContext,
} from '../../../lib/sessionTermDates';
import { DurationField } from '../DurationField';
import { ScormUploading, type ScormUploadState } from '../AddSessionModal/ScormUploading';
import {
  SessionStepsField,
  SESSION_STEP_LIMIT,
  createSessionStep,
  describeBackgroundUploads,
  getSaveErrorMessage,
  stepDraftFromContentStep,
  useLiveSessionSteps,
  type SessionStepDraft,
} from '../SessionStepsField';
import { ScormPreviewModal } from '../ScormPreviewModal';
import {
  ScormSettingsFields,
  type ScormAttemptsMode,
  type ScormCompletionRule,
  type ScormResumeBehavior,
} from '../ScormSettingsFields';
import styles from './EditSessionModal.module.scss';

const initialScormUploadState: ScormUploadState = {
  progress: 0,
  fileName: '',
  fileSize: 0,
  error: null,
  state: 'idle',
};

interface EditSessionModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  lesson: Lesson | null;
  groupId?: number;
  // Session dates and deadlines must lie within it
  intakeTerm?: ModuleIntakeTerm | null;
  isLessonLoading?: boolean;
  onSuccess?: () => void;
}

interface SessionFormData {
  title: string;
  description: string;
  date: CalendarDate | null;
  startTime: Time | null;
  duration: number;
  format: LessonFormat;
  locationId?: number;
  roomId?: number;
  conferenceLink: string;
  isOnlyVideo: boolean;
}

interface HomeworkFormData {
  duration: number;
  isExam: boolean;
  isResubmission: boolean;
  deadline: CalendarDate | null;
  numberOfAttempts: number;
  assignmentText: string;
  assignmentDescription: string;
}

const REQUIRED_MESSAGE = 'This field is required';

const sessionSchema = yup.object({
  title: yup.string().trim().required(REQUIRED_MESSAGE).max(255),
  description: yup.string().trim().required(REQUIRED_MESSAGE),
  date: yup.mixed().required(REQUIRED_MESSAGE).test('within-term', '', testSessionDateWithinTerm),
  startTime: yup
    .mixed()
    .required(REQUIRED_MESSAGE)
    .test('slot-end-within-term', '', testSlotEndWithinTerm),
  duration: yup.number().min(1, REQUIRED_MESSAGE).required(REQUIRED_MESSAGE),
  format: yup.mixed<LessonFormat>().oneOf(['online', 'offline', 'meeting']).required(),
  locationId: yup.number().optional(),
  roomId: yup.number().optional(),
  conferenceLink: yup.string().optional(),
  isOnlyVideo: yup.boolean().optional(),
}) as yup.ObjectSchema<SessionFormData>;

const homeworkSchema = yup.object({
  isExam: yup.boolean(),
  isResubmission: yup.boolean(),
  deadline: yup.mixed().nullable().test('within-term', '', testDeadlineWithinTerm),
  numberOfAttempts: yup.number().min(1).optional(),
  assignmentText: yup.string().optional(),
  assignmentDescription: yup.string().optional(),
}) as yup.ObjectSchema<HomeworkFormData>;

const offlineSwitchText = (stepsCount: number) => {
  const removed = stepsCount > 2 ? `Steps 2-${stepsCount}` : 'Step 2';
  return `Offline sessions support only one step. If you switch, Step 1 will be kept and ${removed} will be permanently deleted, including the progress of students who have already started them. This action cannot be undone`;
};

export const EditSessionModal = memo(
  ({
    isOpen,
    onOpenChange,
    lesson,
    groupId,
    intakeTerm,
    isLessonLoading = false,
    onSuccess,
  }: EditSessionModalProps) => {
    const { toast } = useToast();
    const { termBounds, termLabel } = useSessionTermDates(intakeTerm);
    const [updateLesson, { isLoading }] = useUpdateLessonMutation();
    const isStepsMode = lesson?.contentMode === 'steps';
    const [steps, setSteps] = useState<SessionStepDraft[]>([]);
    // Steps of an existing lesson are saved action by action, not with "Save changes"
    const { actions: liveStepActions, isBusy: isSavingSteps } = useLiveSessionSteps({
      lessonId: lesson?.id,
      lessonTitle: lesson?.title,
      setSteps,
    });
    // Video and SCORM uploads keep running after the modal closes: they inform, but never block it
    const stepServerIds = steps.map((step) => step.serverId);
    const lessonVideoUploads = useStepVideoUploadsFor(stepServerIds).filter(isActiveUpload);
    const lessonScormUploads = useStepScormUploadsFor(stepServerIds).filter(isActiveScormUpload);
    const backgroundUploads = describeBackgroundUploads(
      lessonVideoUploads.length,
      lessonScormUploads.length,
    );
    const [offlineSwitchOpen, setOfflineSwitchOpen] = useState(false);
    const [isSwitchingOffline, setIsSwitchingOffline] = useState(false);
    // Forms are filled once per opened lesson, so background refetches don't wipe unsaved edits
    const initializedLessonIdRef = useRef<number | null>(null);
    const [activeTab, setActiveTab] = useState<string>('session');
    const [sessionFiles, setSessionFiles] = useState<File[]>([]);
    const [homeworkFiles, setHomeworkFiles] = useState<File[]>([]);
    const [removedLessonAttachmentKeys, setRemovedLessonAttachmentKeys] = useState<string[]>([]);
    const [removedAssignmentAttachmentKeys, setRemovedAssignmentAttachmentKeys] = useState<string[]>(
      [],
    );
    const [scormFile, setScormFile] = useState<File | null>(null);
    const [scormPackageUrl, setScormPackageUrl] = useState('');
    const [removeScormPackage, setRemoveScormPackage] = useState(false);
    const [scormAttempts, setScormAttempts] = useState<ScormAttemptsMode>('unlimited');
    const [scormAttemptsCount, setScormAttemptsCount] = useState(1);
    const [scormResumeBehavior, setScormResumeBehavior] = useState<ScormResumeBehavior>('resume');
    const [scormCompletionRule, setScormCompletionRule] = useState<ScormCompletionRule>('score');
    const [scormCompletionScorePercent, setScormCompletionScorePercent] = useState(70);
    const [removeAssignment, setRemoveAssignment] = useState(false);
    const [scormUploadState, setScormUploadState] =
      useState<ScormUploadState>(initialScormUploadState);
    const scormProgressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const [scormPreviewOpen, setScormPreviewOpen] = useState(false);
    const [scormPreviewUrl, setScormPreviewUrl] = useState<string | null>(null);
    const [getScormPreviewUrl, { isLoading: isScormPreviewLoading }] =
      useLazyGetScormPreviewUrlQuery();
    const { data: locationsData } = useGetLocationsAllQuery();
    const sessionDataBaseFiles = useMemo<FileInfo[]>(
      () =>
        (lesson?.attachments ?? []).filter((file) => !removedLessonAttachmentKeys.includes(file.key)),
      [lesson?.attachments, removedLessonAttachmentKeys],
    );
    const homeworkDataBaseFiles = useMemo<FileInfo[]>(
      () =>
        (lesson?.assignment?.attachments ?? []).filter(
          (file) => !removedAssignmentAttachmentKeys.includes(file.key),
        ),
      [lesson?.assignment?.attachments, removedAssignmentAttachmentKeys],
    );

    const hasAssignmentSubmissions =
      lesson?.hasAssignmentSubmissions === true ||
      (lesson as { has_assignment_submissions?: boolean })?.has_assignment_submissions === true;
    const canRemoveAssignment =
      !!lesson?.assignment && !lesson?.isExam && !hasAssignmentSubmissions;

    const {
      control: sessionControl,
      handleSubmit: handleSessionSubmit,
      formState: { errors: sessionErrors },
      reset: resetSession,
      watch: watchSession,
      trigger: triggerSession,
      setValue: setSessionValue,
      getValues: getSessionValues,
      getFieldState: getSessionFieldState,
    } = useForm<SessionFormData, SessionDatesContext>({
      resolver: yupResolver(sessionSchema) as any,
      context: { termBounds, termLabel },
      mode: 'onTouched',
      defaultValues: {
        title: '',
        description: '',
        date: null,
        startTime: null,
        duration: 0,
        format: 'online',
        locationId: undefined,
        roomId: undefined,
        conferenceLink: '',
        isOnlyVideo: false,
      },
    });

    const watchedSessionDate = watchSession('date');
    const sessionDatePickerProps = getSessionDatePickerProps(termBounds);
    const deadlinePickerProps = getSessionDatePickerProps(termBounds, watchedSessionDate);
    const deadlineHint = formatDeadlineHint(termBounds, watchedSessionDate);
    const revalidateStartTime = () => {
      if (sessionErrors.startTime) void triggerSession('startTime');
    };

    const {
      control: homeworkControl,
      handleSubmit: handleHomeworkSubmit,
      formState: { errors: homeworkErrors },
      reset: resetHomework,
      setValue: setHomeworkValue,
      getValues: getHomeworkValues,
    } = useForm<HomeworkFormData, SessionDatesContext>({
      resolver: yupResolver(homeworkSchema) as any,
      context: { termBounds, termLabel, sessionDate: watchedSessionDate },
      defaultValues: {
        isExam: false,
        isResubmission: false,
        deadline: null,
        numberOfAttempts: 1,
        assignmentText: '',
        assignmentDescription: '',
      },
    });

    const watchedSessionFormat = watchSession('format');
    const watchedLocationId = watchSession('locationId');
    const watchedRoomId = watchSession('roomId');
    const locations = locationsData?.data?.locations ?? [];
    const availableLocations = locations.filter((location) => !location.blocked);
    const locationItems = availableLocations.map((location) => ({
      key: String(location.id),
      label: location.title,
    }));
    const selectedLocation = availableLocations.find(
      (location) => location.id === watchedLocationId,
    );
    const roomItems =
      selectedLocation?.rooms
        .filter((room) => room.isAvailable)
        .map((room) => ({
          key: String(room.id),
          label: `${room.number} (${room.capacity})`,
        })) ?? [];

    useLayoutEffect(() => {
      if (!isOpen) {
        initializedLessonIdRef.current = null;
        return;
      }
      if (!lesson || isLessonLoading || initializedLessonIdRef.current === lesson.id) return;
      initializedLessonIdRef.current = lesson.id;
      const raw = lesson as unknown as Record<string, unknown>;
      const getStr = (key: string) => {
        const camel = raw[key];
        if (camel !== null && typeof camel === 'string') return camel;
        const snake =
          raw[
          key
            .replace(/([A-Z])/g, '_$1')
            .toLowerCase()
            .replace(/^_/, '')
          ];
        return snake !== null && typeof snake === 'string' ? snake : null;
      };
      const getNum = (key: string) => {
        const camel = raw[key];
        if (typeof camel === 'number') return camel;
        if (typeof camel === 'string' && camel.trim() !== '') return Number(camel);
        const snake =
          raw[
          key
            .replace(/([A-Z])/g, '_$1')
            .toLowerCase()
            .replace(/^_/, '')
          ];
        if (typeof snake === 'number') return snake;
        if (typeof snake === 'string' && snake.trim() !== '') return Number(snake);
        return undefined;
      };
      const scheduledAt = getStr('scheduledAt') ?? null;
      const scheduledAtParsed =
        scheduledAt !== null && scheduledAt.trim() !== ''
          ? parseISOToDateValueAndTimeAsStored(scheduledAt)
          : { dateValue: null, time: null };
      const endDateRaw = getStr('endDate') ?? null;
      const endDateParsed =
        endDateRaw !== null && endDateRaw.trim() !== ''
          ? parseISOToDateValueAndTime(endDateRaw)
          : null;
      const roomRaw = raw.room;
      const room =
        roomRaw && typeof roomRaw === 'object' ? (roomRaw as Record<string, unknown>) : null;
      const title = getStr('title') ?? '';
      const description = getStr('description') ?? '';
      const duration =
        typeof lesson.duration === 'number' ? lesson.duration : Number((raw as any).duration) || 0;
      const format = (getStr('format') as LessonFormat | null) ?? lesson.format ?? 'online';
      const locationId = getNum('locationId');
      const roomId =
        getNum('roomId') ?? (typeof room?.id === 'number' ? (room.id as number) : undefined);
      const conferenceLink =
        getStr('link') ?? getStr('conferenceLink') ?? getStr('meetingLink') ?? '';
      const isOnlyVideo =
        lesson.isOnlyVideoLesson ?? (raw.is_only_video_lesson as boolean) ?? false;
      const isExam = lesson.isExam ?? (raw.is_exam as boolean) ?? false;
      const assignment = lesson.assignment ?? (raw.assignment as Lesson['assignment']);
      const assignmentText = assignment?.text ?? getStr('assignmentText') ?? '';
      const assignmentDescription =
        assignment?.description ?? getStr('assignmentDescription') ?? '';
      const attempts = assignment?.attempts ?? 1;
      resetSession({
        title,
        description,
        date: scheduledAtParsed.dateValue,
        startTime: scheduledAtParsed.time,
        duration,
        format,
        roomId,
        locationId,
        conferenceLink,
        isOnlyVideo,
      });
      resetHomework({
        isExam,
        isResubmission: false,
        deadline: endDateParsed?.dateValue ?? null,
        numberOfAttempts: attempts,
        assignmentText,
        assignmentDescription,
      });
      setRemovedLessonAttachmentKeys([]);
      setRemovedAssignmentAttachmentKeys([]);
      setRemoveAssignment(false);
      setScormFile(null);
      setScormPackageUrl('');
      setRemoveScormPackage(false);
      setScormUploadState(initialScormUploadState);
      const scormRaw = (raw.scormPackage ?? raw.scorm_package) as Record<string, unknown> | null;
      const hasAssessment =
        (scormRaw?.hasAssessment as boolean | undefined) ??
        (scormRaw?.has_assessment as boolean | undefined) ??
        lesson.scormPackage?.hasAssessment;
      const passingScoreRaw =
        (scormRaw?.passingScore as number | string | undefined) ??
        (scormRaw?.passing_score as number | string | undefined) ??
        lesson.scormPackage?.passingScore;
      const passingScore =
        typeof passingScoreRaw === 'number'
          ? passingScoreRaw
          : typeof passingScoreRaw === 'string' && passingScoreRaw.trim() !== ''
            ? Number(passingScoreRaw)
            : undefined;
      setScormAttempts('unlimited');
      setScormAttemptsCount(1);
      setScormResumeBehavior('resume');
      setScormCompletionRule(hasAssessment === false ? 'completed' : 'score');
      setScormCompletionScorePercent(
        typeof passingScore === 'number' && !Number.isNaN(passingScore) ? passingScore : 70,
      );
      setSteps(
        lesson.steps?.length
          ? lesson.steps.map(stepDraftFromContentStep)
          : [createSessionStep()],
      );
      setOfflineSwitchOpen(false);
      setActiveTab(isExam ? 'homework' : 'session');
    }, [lesson, isOpen, isLessonLoading, resetSession, resetHomework]);

    useEffect(() => {
      if (watchedSessionFormat !== 'offline') {
        setSessionValue('locationId', undefined);
        setSessionValue('roomId', undefined);
      }
      if (watchedSessionFormat !== 'meeting') {
        setSessionValue('conferenceLink', '');
      }
    }, [watchedSessionFormat, setSessionValue]);

    useEffect(() => {
      if (!watchedRoomId || watchedSessionFormat !== 'offline' || watchedLocationId) return;
      const matchedLocation = availableLocations.find((location) =>
        location.rooms.some((room) => room.id === watchedRoomId),
      );
      if (matchedLocation) {
        setSessionValue('locationId', matchedLocation.id);
      }
    }, [
      watchedRoomId,
      watchedSessionFormat,
      watchedLocationId,
      availableLocations,
      setSessionValue,
    ]);

    const clearScormProgressInterval = useCallback(() => {
      if (scormProgressIntervalRef.current) {
        clearInterval(scormProgressIntervalRef.current);
        scormProgressIntervalRef.current = null;
      }
    }, []);

    useEffect(
      () => () => {
        clearScormProgressInterval();
      },
      [clearScormProgressInterval],
    );

    const handleScormFileSelect = useCallback((file: File | null) => {
      setScormFile(file);
      setScormUploadState(
        file
          ? {
            ...initialScormUploadState,
            fileName: file.name,
            fileSize: file.size,
          }
          : initialScormUploadState,
      );
      if (file) setRemoveScormPackage(false);
    }, []);

    const handleScormRemoveFile = useCallback(() => {
      setScormFile(null);
      setScormUploadState(initialScormUploadState);
      clearScormProgressInterval();
    }, [clearScormProgressInterval]);

    const scormPackage = lesson?.scormPackage;
    const scormStatus = scormPackage?.processingStatus ?? null;
    const isScormProcessing = scormStatus === 'queued' || scormStatus === 'processing';
    const isBusy =
      isLoading ||
      isSavingSteps ||
      scormUploadState.state === 'preparing' ||
      scormUploadState.state === 'uploading';

    const handleClose = () => {
      if (isBusy) return;
      resetSession();
      resetHomework();
      setSessionFiles([]);
      setHomeworkFiles([]);
      setScormFile(null);
      setScormPackageUrl('');
      setRemoveScormPackage(false);
      setScormAttempts('unlimited');
      setScormAttemptsCount(1);
      setScormResumeBehavior('resume');
      setScormCompletionRule('score');
      setScormCompletionScorePercent(70);
      setRemoveAssignment(false);
      setScormUploadState(initialScormUploadState);
      setScormPreviewOpen(false);
      setScormPreviewUrl(null);
      setRemovedLessonAttachmentKeys([]);
      setRemovedAssignmentAttachmentKeys([]);
      setSteps([]);
      setOfflineSwitchOpen(false);
      initializedLessonIdRef.current = null;
      setActiveTab('session');
      onOpenChange(false);
    };

    // Offline sessions keep one step: the other steps are deleted right away
    const switchToOffline = async () => {
      setIsSwitchingOffline(true);
      try {
        for (const step of steps.slice(1)) {
          if (!(await liveStepActions.deleteStep(step))) return;
        }
        setSessionValue('format', 'offline');
        setOfflineSwitchOpen(false);
      } finally {
        setIsSwitchingOffline(false);
      }
    };

    const handleRemoveLessonDataBaseFile = (key: string) => {
      setRemovedLessonAttachmentKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
    };

    const handleRemoveAssignmentDataBaseFile = (key: string) => {
      setRemovedAssignmentAttachmentKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
    };

    const openScormPreview = async (packageId: number) => {
      try {
        const result = await getScormPreviewUrl(packageId).unwrap();
        let url = result?.data?.launchUrl;
        if (url) {
          if (url.startsWith('/') && !url.startsWith('//')) {
            const apiBase = import.meta.env.VITE_API_URL_BASE;
            url = `${apiBase}${url}`;
          }
          setScormPreviewUrl(url);
          setScormPreviewOpen(true);
        }
      } catch {
        toast({
          title: 'Error',
          description: 'Failed to load SCORM preview',
          color: 'danger',
        });
      }
    };

    const handleOpenScormPreview = () => {
      const pkg = lesson?.scormPackage;
      if (!pkg?.id || pkg.processingStatus !== 'ready' || !pkg.isValid) return;
      openScormPreview(pkg.id);
    };

    const onSessionSubmit = async (data: SessionFormData) => {
      if (!lesson) return;

      try {
        const currentDate = getSessionValues('date');
        const currentStartTime = getSessionValues('startTime');

        const date = currentDate !== null && currentDate !== undefined ? currentDate : data.date;
        const startTime =
          currentStartTime !== null && currentStartTime !== undefined
            ? currentStartTime
            : data.startTime;

        if (!date || !startTime) {
          toast({
            title: 'Error',
            description: 'Date and start time are required',
            color: 'danger',
          });
          return;
        }

        const scheduledAt = combineDateAndTimeAsUTC(date, startTime);
        const duration = data.duration || 0;
        const format: LessonFormat = data.format;
        if (format === 'meeting' && !data.conferenceLink.trim()) {
          toast({
            title: 'Error',
            description: 'Conference link is required for meeting',
            color: 'danger',
          });
          return;
        }
        if (format === 'offline' && !data.roomId) {
          toast({
            title: 'Error',
            description: 'Select location and room for offline session',
            color: 'danger',
          });
          return;
        }

        if (!scheduledAt) {
          toast({
            title: 'Error',
            description: 'Invalid date or time',
            color: 'danger',
          });
          return;
        }

        const result = await updateLesson({
          lessonId: lesson.id,
          body: {
            title: data.title,
            description: data.description,
            duration: data.duration,
            format,
            roomId: format === 'offline' ? data.roomId : undefined,
            conferenceLink: format === 'meeting' ? data.conferenceLink.trim() : undefined,
            scheduledAt,
            groupId: groupId || lesson.groupLessonId || undefined,
            isOnlyVideoLesson: format === 'online' && Boolean(data.isOnlyVideo),
            ...(lesson.assignment
              ? {
                assignmentText: lesson.assignment.text,
                assignmentDescription: lesson.assignment.description,
                assignmentAttempts: lesson.assignment.attempts || 1,
              }
              : {}),
            lessonFiles: sessionFiles.length > 0 ? sessionFiles : undefined,
            removeLessonAttachmentKeys:
              removedLessonAttachmentKeys.length > 0 ? removedLessonAttachmentKeys : undefined,
          },
        }).unwrap();

        toast({
          title: 'Success',
          description: result.message || 'Session updated successfully',
          color: 'success',
        });

        handleClose();
        onSuccess?.();
      } catch (error: any) {
        toast({
          title: 'Error',
          description: error?.data?.message || 'Failed to update session',
          color: 'danger',
        });
      }
    };

    const onHomeworkSubmit = async (data: HomeworkFormData) => {
      if (!lesson) return;

      try {
        const currentDeadline = getHomeworkValues('deadline');
        const deadline =
          currentDeadline !== null && currentDeadline !== undefined
            ? currentDeadline
            : data.deadline;

        const sessionFormValues = getSessionValues();
        const sessionDate = sessionFormValues?.date;
        const sessionStartTime = sessionFormValues?.startTime;
        const format: LessonFormat = sessionFormValues?.format ?? 'online';
        const roomId = sessionFormValues?.roomId;
        const conferenceLink = sessionFormValues?.conferenceLink?.trim() || '';

        if (!sessionDate || !sessionStartTime) {
          toast({
            title: 'Error',
            description:
              'Session date and start time are required. Please fill them on the Session tab.',
            color: 'danger',
          });
          return;
        }
        if (format === 'offline' && !roomId) {
          toast({
            title: 'Error',
            description: 'Select location and room for offline session',
            color: 'danger',
          });
          return;
        }
        if (format === 'meeting' && !conferenceLink) {
          toast({
            title: 'Error',
            description: 'Conference link is required for meeting',
            color: 'danger',
          });
          return;
        }

        const scheduledAt = combineDateAndTimeAsUTC(sessionDate, sessionStartTime);

        const endDate = deadline ? combineDateAndTime(deadline, parseTime('23:59')) : undefined;

        if (!scheduledAt) {
          toast({
            title: 'Error',
            description: 'Invalid date',
            color: 'danger',
          });
          return;
        }

        const sessionTitle = sessionFormValues?.title?.trim() || lesson.title;
        const sessionDescription =
          sessionFormValues?.description?.trim() || lesson.description || '';
        const duration = Number(sessionFormValues?.duration) || 0;
        // Without an assignment and with nothing filled in, don't create an empty one
        const hasAssignmentInput =
          !!lesson.assignment ||
          !!data.assignmentText?.trim() ||
          !!data.assignmentDescription?.trim() ||
          homeworkFiles.length > 0;

        const result = await updateLesson({
          lessonId: lesson.id,
          body: {
            title: sessionTitle,
            description: sessionDescription,
            duration,
            isExam: removeAssignment ? false : data.isExam,
            format,
            roomId: format === 'offline' ? roomId : undefined,
            conferenceLink: format === 'meeting' ? conferenceLink : undefined,
            scheduledAt,
            endDate: removeAssignment ? undefined : endDate || undefined,
            groupId: groupId || lesson.groupLessonId || undefined,
            isOnlyVideoLesson: format === 'online' && Boolean(sessionFormValues?.isOnlyVideo),
            ...(removeAssignment
              ? { removeAssignment: true }
              : hasAssignmentInput
                ? {
                  assignmentText: data.assignmentText,
                  assignmentDescription: data.assignmentDescription,
                  assignmentAttempts: data.numberOfAttempts || 1,
                  assignmentFiles: homeworkFiles.length > 0 ? homeworkFiles : undefined,
                  removeAssignmentAttachmentKeys:
                    removedAssignmentAttachmentKeys.length > 0
                      ? removedAssignmentAttachmentKeys
                      : undefined,
                }
                : {}),
            lessonFiles: sessionFiles.length > 0 ? sessionFiles : undefined,
            removeLessonAttachmentKeys:
              removedLessonAttachmentKeys.length > 0 ? removedLessonAttachmentKeys : undefined,
          },
        }).unwrap();

        toast({
          title: 'Success',
          description: isStepsMode
            ? 'Session updated successfully'
            : result.message || 'Homework updated successfully',
          color: 'success',
        });

        handleClose();
        onSuccess?.();
      } catch (error) {
        toast({
          title: 'Error',
          description: getSaveErrorMessage(error, 'Failed to update session'),
          color: 'danger',
        });
      }
    };

    const watchedSessionTitle = watchSession('title');
    const watchedSessionDescription = watchSession('description');
    const watchedSessionStartTime = watchSession('startTime');
    const watchedConferenceLink = watchSession('conferenceLink');
    const hasFallbackScheduledAt = !!lesson?.scheduledAt;
    const hasFallbackRoomId = typeof lesson?.roomId === 'number';
    const fallbackConferenceLink =
      ((lesson as any)?.conferenceLink as string | undefined) ||
      ((lesson as any)?.meetingLink as string | undefined) ||
      '';
    const canSubmitSession =
      !!(watchedSessionTitle?.trim() || lesson?.title?.trim()) &&
      !!(watchedSessionDescription?.trim() || lesson?.description?.trim()) &&
      (!!watchedSessionDate || hasFallbackScheduledAt) &&
      (!!watchedSessionStartTime || hasFallbackScheduledAt) &&
      (watchedSessionFormat !== 'offline' || !!watchedRoomId || hasFallbackRoomId) &&
      (watchedSessionFormat !== 'meeting' ||
        !!watchedConferenceLink?.trim() ||
        !!fallbackConferenceLink.trim());

    const canSubmitHomework = canSubmitSession && (!removeAssignment || canRemoveAssignment);

    const handleStepsModeSave = handleSessionSubmit(
      () => handleHomeworkSubmit(onHomeworkSubmit as any, () => setActiveTab('homework'))(),
      () => setActiveTab('session'),
    );

    const sessionValuesForScorm = getSessionValues();
    const canSubmitScorm =
      (!!sessionValuesForScorm?.date || hasFallbackScheduledAt) &&
      (!!sessionValuesForScorm?.startTime || hasFallbackScheduledAt) &&
      !!(sessionValuesForScorm?.title?.trim() || lesson?.title?.trim()) &&
      !!(sessionValuesForScorm?.description?.trim() || lesson?.description?.trim()) &&
      (scormFile !== null ||
        scormPackageUrl.trim() !== '' ||
        removeScormPackage ||
        !!lesson?.scormPackage);

    const onScormSubmit = async () => {
      if (!lesson) return;
      const sessionValues = getSessionValues();
      const sessionDate = sessionValues?.date;
      const sessionStartTime = sessionValues?.startTime;
      const format: LessonFormat = sessionValues?.format ?? 'online';
      const roomId = sessionValues?.roomId;
      const conferenceLink = sessionValues?.conferenceLink?.trim() || '';
      if (!sessionDate || !sessionStartTime) {
        toast({
          title: 'Error',
          description: 'Fill Date and Start time on the session tab',
          color: 'danger',
        });
        return;
      }
      const scheduledAt = combineDateAndTimeAsUTC(sessionDate, sessionStartTime);
      if (!scheduledAt) {
        toast({
          title: 'Error',
          description: 'Invalid date or time',
          color: 'danger',
        });
        return;
      }

      const file = scormFile;
      try {
        if (file) {
          setScormUploadState((prev) => ({
            ...prev,
            state: 'preparing',
            statusText: 'Preparing for upload...',
          }));
          await new Promise((r) => setTimeout(r, 300));
          setScormUploadState((prev) => ({
            ...prev,
            state: 'uploading',
            statusText: 'Uploading file...',
            progress: 0,
          }));
          let progress = 0;
          scormProgressIntervalRef.current = setInterval(() => {
            progress = Math.min(progress + Math.random() * 8 + 4, 90);
            setScormUploadState((prev) => ({
              ...prev,
              progress: Math.round(progress),
              state: 'uploading',
              statusText: 'Uploading file...',
            }));
          }, 200);
        }

        const title = (sessionValues?.title?.trim() || lesson.title || '').trim();
        const description = (sessionValues?.description?.trim() || lesson.description || '').trim();
        const duration = Number(sessionValues?.duration) ?? lesson.duration;
        const body: UpdateLessonRequest = {
          title,
          description,
          duration,
          format,
          roomId: format === 'offline' ? roomId : undefined,
          conferenceLink: format === 'meeting' ? conferenceLink : undefined,
          scheduledAt,
          groupId: groupId ?? lesson.groupLessonId ?? undefined,
          isOnlyVideoLesson:
            format === 'online' &&
            Boolean(sessionValues?.isOnlyVideo ?? lesson.isOnlyVideoLesson),
          ...(lesson.assignment
            ? {
              assignmentText: lesson.assignment.text,
              assignmentDescription: lesson.assignment.description,
              assignmentAttempts: lesson.assignment.attempts ?? 1,
            }
            : {}),
          ...(file
            ? { scormPackageFile: file }
            : scormPackageUrl.trim()
              ? { scormPackageUrl: scormPackageUrl.trim() }
              : removeScormPackage
                ? { removeScormPackage: true }
                : {}),
          ...(!removeScormPackage
            ? {
              scormHasAssessment: scormCompletionRule === 'score',
              scormPassingScore:
                scormCompletionRule === 'score' ? scormCompletionScorePercent : undefined,
            }
            : {}),
        };
        const result = await updateLesson({
          lessonId: lesson.id,
          body,
        }).unwrap();

        clearScormProgressInterval();
        if (file) {
          setScormUploadState((prev) => ({
            ...prev,
            progress: 100,
            state: 'completed',
            statusText: 'Upload completed',
          }));
          await new Promise((r) => setTimeout(r, 400));
        }
        toast({
          title: 'Success',
          description: result.message || 'Session updated successfully',
          color: 'success',
        });
        handleClose();
        onSuccess?.();
      } catch (error: any) {
        clearScormProgressInterval();
        if (file) {
          setScormUploadState((prev) => ({
            ...prev,
            state: 'error',
            error: error?.data?.message || 'Failed to update session',
          }));
        } else {
          toast({
            title: 'Error',
            description: error?.data?.message || 'Failed to update session',
            color: 'danger',
          });
        }
      }
    };

    if (!lesson) return null;

    return (
      <>
        <Modal
          isOpen={isOpen}
          onOpenChange={(open) => {
            if (!open) handleClose();
          }}
          placement="center"
          motionProps={scaleInOut}
          size="2xl"
          backdrop="opaque"
          scrollBehavior="inside"
          isDismissable={!isBusy}
          isKeyboardDismissDisabled={isBusy}
          hideCloseButton={isBusy}
          classNames={{
            base: 'p-6 flex flex-col gap-5 max-w-[600px] h-[800px]',
            closeButton: styles.closeBtn,
            wrapper: styles.modalContent,
          }}
        >
          <ModalContent>
            {() => (
              <>
                <ModalHeader className={styles.title}>
                  {activeTab === 'session'
                    ? 'Edit session'
                    : activeTab === 'scorm'
                      ? 'SCORM'
                      : 'Edit assignments'}
                </ModalHeader>
                <ModalBody className="p-0 flex flex-col gap-6">
                  {isLessonLoading ? (
                    <SectionLoader />
                  ) : (
                    <Tabs
                      aria-label="Session type tabs"
                      selectedKey={activeTab}
                      onSelectionChange={(key) => setActiveTab(key as string)}
                      classNames={{
                        tabList: 'gap-0 bg-transparent w-full',
                        tab: 'px-6 py-3 bg-transparent text-grey-secondary data-[selected=true]:bg-[#1E1E1E] rounded-lg flex-1',
                        tabContent: 'text-grey-secondary data-[selected=true]:text-[#FFFFFF]',
                      }}
                    >
                      <Tab key="session" title="Add new session">
                        <form
                          onSubmit={handleSessionSubmit(onSessionSubmit as any)}
                          className="flex flex-col gap-6"
                          id="session-form"
                          noValidate
                        >
                          <Controller
                            name="format"
                            control={sessionControl}
                            render={({ field }) => (
                              <RadioGroup
                                value={field.value}
                                onValueChange={(value) => {
                                  if (isStepsMode && value === 'offline' && steps.length > 1) {
                                    setOfflineSwitchOpen(true);
                                    return;
                                  }
                                  field.onChange(value as LessonFormat);
                                }}
                                classNames={{ wrapper: 'flex flex-row gap-6' }}
                              >
                                <Radio
                                  value="online"
                                  classNames={{ label: 'text-sm !text-black-headline' }}
                                >
                                  Online session
                                </Radio>
                                <Radio
                                  value="offline"
                                  classNames={{ label: 'text-sm !text-black-headline' }}
                                >
                                  Offline session
                                </Radio>
                                <Radio
                                  value="meeting"
                                  classNames={{ label: 'text-sm !text-black-headline' }}
                                >
                                  Meeting
                                </Radio>
                              </RadioGroup>
                            )}
                          />
                          {watchSession('format') === 'online' && (
                            <Controller
                              name="isOnlyVideo"
                              control={sessionControl}
                              render={({ field }) => (
                                <Checkbox
                                  isSelected={field.value}
                                  onValueChange={field.onChange}
                                  classNames={{
                                    base: 'w-full',
                                    label: 'text-sm !text-black-headline',
                                  }}
                                >
                                  Only video
                                </Checkbox>
                              )}
                            />
                          )}

                          <Controller
                            name="title"
                            control={sessionControl}
                            render={({ field }) => (
                              <CustomInput
                                {...field}
                                label="Session Title"
                                labelPlacement="outside"
                                isRequired
                                placeholder="Enter session title"
                                errorMessage={sessionErrors.title?.message}
                                showError={!!sessionErrors.title}
                              />
                            )}
                          />

                          <Controller
                            name="description"
                            control={sessionControl}
                            render={({ field }) => (
                              <CustomInput
                                {...field}
                                label="Session Description"
                                labelPlacement="outside"
                                isRequired
                                placeholder="Enter session description"
                                errorMessage={sessionErrors.description?.message}
                                showError={!!sessionErrors.description}
                              />
                            )}
                          />

                          <div className="flex flex-col gap-2">
                            <Controller
                              name="date"
                              control={sessionControl}
                              render={({ field }) => (
                                <CustomDatepicker
                                  label="Date"
                                  labelPlacement="outside"
                                  isRequired
                                  value={field.value}
                                  onChange={(value) => {
                                    field.onChange(value);
                                    revalidateStartTime();
                                  }}
                                  onBlur={field.onBlur}
                                  errorMessage={sessionErrors.date?.message}
                                  showError={!!sessionErrors.date}
                                  {...sessionDatePickerProps}
                                  placeholderValue={null}
                                />
                              )}
                            />
                            <FieldHint>{formatSessionDateHint(termBounds)}</FieldHint>
                          </div>
                          <div className="flex gap-3">
                            <div className="w-full">
                              <Controller
                                name="startTime"
                                control={sessionControl}
                                render={({ field }) => (
                                  <CustomTimeInput
                                    label="Start time"
                                    labelPlacement="outside"
                                    isRequired
                                    value={field.value}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    errorMessage={sessionErrors.startTime?.message}
                                    showError={!!sessionErrors.startTime}
                                  />
                                )}
                              />
                              {!sessionErrors.startTime && (
                                <p className="text-xs text-grey-secondary mt-1">
                                  Time is shown in your local time zone
                                </p>
                              )}
                            </div>
                            <div className="w-full">
                              <Controller
                                name="duration"
                                control={sessionControl}
                                render={({ field }) => (
                                  <DurationField
                                    value={field.value}
                                    onChange={(value) => {
                                      field.onChange(value);
                                      revalidateStartTime();
                                    }}
                                    onBlur={field.onBlur}
                                    errorMessage={sessionErrors.duration?.message}
                                    isRequired
                                  />
                                )}
                              />
                            </div>
                          </div>
                          {watchedSessionFormat === 'offline' && (
                            <div className="flex gap-3">
                              <div className="w-full">
                                <Controller
                                  name="locationId"
                                  control={sessionControl}
                                  render={({ field }) => (
                                    <CustomSelect
                                      label="Campus"
                                      labelPlacement="outside"
                                      isRequired
                                      placeholder="Enter Campus"
                                      style="black"
                                      items={locationItems}
                                      selectedKeys={
                                        field.value ? new Set([String(field.value)]) : new Set()
                                      }
                                      onSelectionChange={(keys) => {
                                        const value = Array.from(keys as Set<string>)[0];
                                        field.onChange(value ? Number(value) : undefined);
                                        setSessionValue('roomId', undefined);
                                      }}
                                    />
                                  )}
                                />
                              </div>
                              <div className="w-full">
                                <Controller
                                  name="roomId"
                                  control={sessionControl}
                                  render={({ field }) => (
                                    <CustomSelect
                                      label="Room"
                                      labelPlacement="outside"
                                      isRequired
                                      placeholder="Enter Room"
                                      style="black"
                                      items={roomItems}
                                      selectedKeys={
                                        field.value ? new Set([String(field.value)]) : new Set()
                                      }
                                      onSelectionChange={(keys) => {
                                        const value = Array.from(keys as Set<string>)[0];
                                        field.onChange(value ? Number(value) : undefined);
                                      }}
                                    />
                                  )}
                                />
                              </div>
                            </div>
                          )}
                          {watchedSessionFormat === 'meeting' && (
                            <Controller
                              name="conferenceLink"
                              control={sessionControl}
                              render={({ field }) => (
                                <CustomInput
                                  {...field}
                                  label="Conference link"
                                  labelPlacement="outside"
                                  isRequired
                                  placeholder="https://meet.google.com/..."
                                />
                              )}
                            />
                          )}
                          {(isStepsMode
                            ? (watchedSessionFormat === 'online' && watchSession('isOnlyVideo')) ||
                              sessionDataBaseFiles.length > 0
                            : watchedSessionFormat !== 'meeting') && (
                            <div className="flex flex-col gap-2">
                              <label className="text-sm font-medium text-black-headline">
                                Upload files
                              </label>
                              <Dropzone
                                files={sessionFiles}
                                setFiles={setSessionFiles}
                                dataBaseFiles={sessionDataBaseFiles}
                                handleRemoveDataBaseFile={handleRemoveLessonDataBaseFile}
                                multiple
                                accept={SESSION_ATTACHMENT_ACCEPT}
                                withPreviewList
                                className="p-12"
                              >
                                <div className="flex items-center justify-center gap-6 ">
                                  <Svg
                                    Icon={UploadVideoIcon}
                                    width={48}
                                    height={48}
                                    className="text-red"
                                  />
                                  <div className="flex flex-col gap-y-2">
                                    <p className="text-black font-bold leading-[1] tracking-[-5%]">
                                      Upload or Drag and Drop your File
                                    </p>
                                    <p className="text-grey-description text-sm leading-[1] tracking-[-5%]">
                                      {MAX_FILE_SIZE_HINT}
                                    </p>
                                    <p className="text-grey-description text-sm leading-[1] tracking-[-5%]">
                                      {SESSION_ATTACHMENT_TYPES_HINT}
                                    </p>
                                  </div>
                                </div>
                              </Dropzone>
                            </div>
                          )}
                          {isStepsMode && (
                            <>
                              <Divider />
                              <SessionStepsField
                                steps={steps}
                                onChange={setSteps}
                                maxSteps={
                                  watchedSessionFormat === 'offline' ? 1 : SESSION_STEP_LIMIT
                                }
                                warnAboutProgress
                                serverSteps={lesson.steps}
                                onPreviewScorm={openScormPreview}
                                isScormPreviewLoading={isScormPreviewLoading}
                                live={liveStepActions}
                              />
                            </>
                          )}
                        </form>
                      </Tab>

                      <Tab key="homework" title="Assignments">
                        <form
                          onSubmit={handleHomeworkSubmit(onHomeworkSubmit as any)}
                          className="flex flex-col gap-6"
                          id="homework-form"
                        >
                          <div
                            className={`flex gap-4 ${removeAssignment ? 'opacity-50 pointer-events-none' : ''}`}
                          >
                            <Controller
                              name="isExam"
                              control={homeworkControl}
                              render={({ field }) => (
                                <Checkbox
                                  isSelected={field.value === false}
                                  isDisabled={removeAssignment}
                                  onValueChange={(val) => {
                                    if (val) {
                                      field.onChange(false);
                                    }
                                  }}
                                  classNames={{
                                    base: 'w-full',
                                    label: 'text-sm !text-black-headline',
                                  }}
                                >
                                  Formative
                                </Checkbox>
                              )}
                            />
                            <Controller
                              name="isExam"
                              control={homeworkControl}
                              render={({ field }) => (
                                <Checkbox
                                  isSelected={field.value === true}
                                  isDisabled={removeAssignment}
                                  onValueChange={(val) => {
                                    if (val) {
                                      field.onChange(true);
                                    }
                                  }}
                                  classNames={{
                                    base: 'w-full',
                                    label: 'text-sm !text-black-headline',
                                  }}
                                >
                                  Summative
                                </Checkbox>
                              )}
                            />
                          </div>

                          <div className="flex flex-col gap-2">
                            <Controller
                              name="deadline"
                              control={homeworkControl}
                              render={({ field }) => (
                                <CustomDatepicker
                                  label="Deadline"
                                  labelPlacement="outside"
                                  isDisabled={removeAssignment}
                                  value={field.value}
                                  onChange={field.onChange}
                                  errorMessage={homeworkErrors.deadline?.message}
                                  showError={!!homeworkErrors.deadline}
                                  {...deadlinePickerProps}
                                  placeholderValue={null}
                                />
                              )}
                            />
                            <FieldHint>{deadlineHint}</FieldHint>
                          </div>

                          <Controller
                            name="numberOfAttempts"
                            control={homeworkControl}
                            render={({ field }) => (
                              <CustomInput
                                {...field}
                                value={field.value?.toString() || ''}
                                onChange={(e) => field.onChange(Number(e.target.value) || 1)}
                                type="number"
                                isDisabled={removeAssignment}
                                label="Number of attempts"
                                labelPlacement="outside"
                                placeholder="Enter number of attempts"
                                errorMessage={homeworkErrors.numberOfAttempts?.message}
                                showError={!!homeworkErrors.numberOfAttempts}
                              />
                            )}
                          />

                          <Controller
                            name="assignmentText"
                            control={homeworkControl}
                            render={({ field }) => (
                              <CustomInput
                                {...field}
                                isDisabled={removeAssignment}
                                label="Assignment"
                                labelPlacement="outside"
                                placeholder="Enter homework assignment"
                                errorMessage={homeworkErrors.assignmentText?.message}
                                showError={!!homeworkErrors.assignmentText}
                              />
                            )}
                          />

                          <Controller
                            name="assignmentDescription"
                            control={homeworkControl}
                            render={({ field }) => (
                              <CustomTextarea
                                {...field}
                                isDisabled={removeAssignment}
                                label="Assignment Description"
                                labelPlacement="outside"
                                placeholder="Enter homework description"
                                errorMessage={homeworkErrors.assignmentDescription?.message}
                                showError={!!homeworkErrors.assignmentDescription}
                              />
                            )}
                          />

                          <div
                            className={`flex flex-col gap-2 ${removeAssignment ? 'opacity-50 pointer-events-none' : ''}`}
                          >
                            <label className="text-sm font-medium text-black-headline">
                              Upload files
                            </label>
                            <Dropzone
                              files={homeworkFiles}
                              setFiles={setHomeworkFiles}
                              dataBaseFiles={homeworkDataBaseFiles}
                              handleRemoveDataBaseFile={handleRemoveAssignmentDataBaseFile}
                              multiple
                              accept={SESSION_ATTACHMENT_ACCEPT}
                              withPreviewList
                              className="p-12"
                            >
                              <div className="flex items-center justify-center gap-6 ">
                                <Svg
                                  Icon={UploadVideoIcon}
                                  width={48}
                                  height={48}
                                  className="text-red"
                                />
                                <div className="flex flex-col gap-y-2">
                                  <p className="text-black font-bold leading-[1] tracking-[-5%]">
                                    Upload or Drag and Drop your File
                                  </p>
                                  <p className="text-grey-description text-sm leading-[1] tracking-[-5%]">
                                    {MAX_FILE_SIZE_HINT}
                                  </p>
                                  <p className="text-grey-description text-sm leading-[1] tracking-[-5%]">
                                    {SESSION_ATTACHMENT_TYPES_HINT}
                                  </p>
                                </div>
                              </div>
                            </Dropzone>
                          </div>

                          {lesson.assignment && (
                            <div className="flex flex-col gap-2">
                              <Checkbox
                                isSelected={removeAssignment}
                                isDisabled={!canRemoveAssignment}
                                onValueChange={setRemoveAssignment}
                                classNames={{
                                  base: 'w-full',
                                  label: 'text-sm !text-black-headline',
                                }}
                              >
                                Remove assignment
                              </Checkbox>
                              {!canRemoveAssignment && (
                                <p className="text-sm text-grey-secondary">
                                  {lesson.isExam
                                    ? 'Cannot remove assignment from exam lessons.'
                                    : 'Cannot remove assignment while there are student submissions.'}
                                </p>
                              )}
                            </div>
                          )}
                        </form>
                      </Tab>

                      {!isStepsMode && (
                      <Tab key="scorm" title="SCORM">
                        <div className="flex flex-col gap-6">
                          {scormPackage && (
                            <div className="flex flex-col gap-2">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm text-grey-secondary">
                                  {scormPackage.title && !isScormProcessing
                                    ? `SCORM package: ${scormPackage.title}${
                                        scormPackage.version ? ` (${scormPackage.version})` : ''
                                      }`
                                    : `SCORM package version: ${scormPackage.version || '—'}`}
                                </p>
                                {scormStatus && (
                                  <span
                                    className="inline-flex h-6 items-center rounded-[6px] px-2 text-xs font-semibold"
                                    style={{
                                      backgroundColor: SCORM_STATUS_BADGE[scormStatus].bg,
                                      color: SCORM_STATUS_BADGE[scormStatus].text,
                                    }}
                                  >
                                    {SCORM_STATUS_BADGE[scormStatus].label}
                                  </span>
                                )}
                              </div>
                              {!scormFile &&
                                !removeScormPackage &&
                                scormStatus === 'ready' &&
                                scormPackage.isValid && (
                                  <Button
                                    className="w-fit bg-white text-gray-500 text-[14px] p-0"
                                    isLoading={isScormPreviewLoading}
                                    disabled={isScormPreviewLoading}
                                    onPress={handleOpenScormPreview}
                                  >
                                    Preview SCORM{' '}
                                    <Svg Icon={OpenedEyeIcon} width={20} height={20} />
                                  </Button>
                                )}
                              {scormStatus === 'failed' && (
                                <p className="text-red-tertiary text-sm">
                                  {scormPackage.processingError ||
                                    scormPackage.validationErrors?.[0] ||
                                    'SCORM package is invalid'}
                                </p>
                              )}
                            </div>
                          )}
                          <div>
                            <h3 className="text-sm font-medium text-black-headline mb-3">
                              Upload a SCORM package
                            </h3>
                            {scormFile ? (
                              <ScormUploading
                                uploadState={scormUploadState}
                                selectedFile={scormFile}
                                onCancel={handleScormRemoveFile}
                              />
                            ) : (
                              <Dropzone
                                setFile={handleScormFileSelect}
                                isScorm
                                className="p-12"
                                disabled={isScormProcessing}
                              >
                                <div className="flex items-center justify-center gap-6">
                                  <Svg
                                    Icon={UploadVideoIcon}
                                    width={48}
                                    height={48}
                                    className="text-red"
                                  />
                                  <div className="flex flex-col gap-y-2">
                                    <p className="text-black font-bold leading-[1] tracking-[-5%]">
                                      Drag and drop your SCORM .zip file here
                                    </p>
                                    <p className="text-grey-description text-sm leading-[1] tracking-[-5%]">
                                      Supported formats: SCORM 1.2 and SCORM 2004
                                    </p>
                                    <p className="text-grey-description text-sm leading-[1] tracking-[-5%]">
                                      The .zip file must include imsmanifest.xml
                                    </p>
                                  </div>
                                </div>
                              </Dropzone>
                            )}
                          </div>
                          <ScormSettingsFields
                            attempts={scormAttempts}
                            onAttemptsChange={setScormAttempts}
                            attemptsCount={scormAttemptsCount}
                            onAttemptsCountChange={setScormAttemptsCount}
                            resumeBehavior={scormResumeBehavior}
                            onResumeBehaviorChange={setScormResumeBehavior}
                            completionRule={scormCompletionRule}
                            onCompletionRuleChange={setScormCompletionRule}
                            completionScorePercent={scormCompletionScorePercent}
                            onCompletionScorePercentChange={setScormCompletionScorePercent}
                            isDisabled={removeScormPackage || isScormProcessing}
                          />
                          {!scormFile && scormPackage && (
                            <Checkbox
                              isSelected={removeScormPackage}
                              isDisabled={isScormProcessing}
                              onValueChange={setRemoveScormPackage}
                              classNames={{
                                base: 'w-full',
                                label: 'text-sm !text-black-headline',
                              }}
                            >
                              Remove current SCORM package
                            </Checkbox>
                          )}
                        </div>
                      </Tab>
                      )}
                    </Tabs>
                  )}
                </ModalBody>
                <ModalFooter className="flex-col gap-3 pt-6">
                  {isStepsMode && (isLoading || isSavingSteps) && (
                    <p className="text-sm text-grey-secondary" role="status">
                      {isLoading ? 'Saving the session...' : 'Saving steps...'}
                    </p>
                  )}
                  {isStepsMode && !isLoading && !isSavingSteps && backgroundUploads && (
                    <p className="text-sm text-grey-secondary" role="status">
                      {backgroundUploads} uploading. You can close this window, uploads continue in
                      the background
                    </p>
                  )}
                  <div className="flex w-full gap-4">
                    <AppButton
                      fullWidth
                      color="white"
                      onPress={handleClose}
                      type="button"
                      disabled={isBusy}
                  >
                      Cancel
                    </AppButton>
                    {isStepsMode ? (
                      <AppButton
                        fullWidth
                        color="black"
                        type="button"
                        isLoading={isBusy}
                        disabled={isLessonLoading || !canSubmitHomework || isBusy}
                        onPress={handleStepsModeSave}
                    >
                        Save changes
                      </AppButton>
                  ) : activeTab === 'session' ? (
                    <AppButton
                      fullWidth
                      color="black"
                      type="button"
                      disabled={isLessonLoading || !canSubmitSession}
                      onPress={async () => {
                        const isValid = await triggerSession();
                        if (isValid) {
                          setActiveTab('homework');
                        }
                      }}
                    >
                      Next
                    </AppButton>
                  ) : activeTab === 'scorm' ? (
                    <AppButton
                      fullWidth
                      color="black"
                      type="button"
                      isLoading={isBusy}
                      disabled={isLessonLoading || !canSubmitScorm || isBusy || isScormProcessing}
                      onPress={onScormSubmit}
                    >
                      Save changes
                    </AppButton>
                  ) : (
                    <AppButton
                      fullWidth
                      color="black"
                      type="button"
                      isLoading={isLoading}
                      disabled={isLessonLoading || !canSubmitHomework}
                      onPress={() => {
                        handleHomeworkSubmit(onHomeworkSubmit as any)();
                      }}
                    >
                      Save changes
                    </AppButton>
                  )}
                  </div>
                </ModalFooter>
              </>
            )}
          </ModalContent>
        </Modal>

        <MessageModal
          isOpen={offlineSwitchOpen}
          onOpenChange={() => setOfflineSwitchOpen(false)}
          title="Switch to Offline?"
          text={offlineSwitchText(steps.length)}
          confirmText="Switch and delete steps"
          variant="red"
          isLoading={isSwitchingOffline}
          toggleVisibility={switchToOffline}
        />

        <ScormPreviewModal
          isOpen={scormPreviewOpen}
          onOpenChange={setScormPreviewOpen}
          previewUrl={scormPreviewUrl}
        />
      </>
    );
  },
);

EditSessionModal.displayName = 'EditSessionModal';
